import React, { useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker } from '~/components/common';
import dayjs from 'dayjs';
import styles from './JourneyHeader.module.scss';

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface JourneyHeaderProps {
  dateRange: DateRange;
  onDateRangeChange: (start: Date | null, end: Date | null) => void;
  isLoading?: boolean;
}

const JourneyHeader: React.FC<JourneyHeaderProps> = ({ dateRange, onDateRangeChange, isLoading = false }) => {
  const { t } = useTranslation('admin/journey');

  const handleStartDateChange = useCallback(
    (date: string | null) => {
      const start = date ? dayjs(date).toDate() : null;
      onDateRangeChange(start, dateRange.end);
    },
    [dateRange.end, onDateRangeChange]
  );

  const handleEndDateChange = useCallback(
    (date: string | null) => {
      const end = date ? dayjs(date).toDate() : null;
      onDateRangeChange(dateRange.start, end);
    },
    [dateRange.start, onDateRangeChange]
  );

  const rangeLabel = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return t('empty.selectDateRangePrompt');
    return `${format(dateRange.start, 'dd/MM/yyyy')} - ${format(dateRange.end, 'dd/MM/yyyy')}`;
  }, [dateRange.end, dateRange.start, t]);

  return (
    <Box className={styles.pageHeaderBar}>
      <Box className={styles.pageHeaderInfo}>
        <Typography component='h2' className={styles.pageHeaderTitle}>
          {t('header.title')}:
        </Typography>
        <Typography component='p' className={styles.pageHeaderRange}>
          {rangeLabel}
        </Typography>
      </Box>

      <Box className='flex flex-wrap items-center gap-3'>
        <Box sx={{ flex: '0 0 180px', minWidth: 160 }}>
          <BaseDatePicker
            portalId='datePicker-portalId'
            placeholder={t('header.startDatePlaceholder')}
            value={dateRange.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined}
            onChange={handleStartDateChange}
            dateFormat='YYYY-MM-DD'
            disabled={isLoading}
            className='w-full'
          />
        </Box>
        <Box className='text-[#ffffff] text-sm'>-</Box>
        <Box sx={{ flex: '0 0 180px', minWidth: 160 }}>
          <BaseDatePicker
            portalId='datePicker-portalId'
            placeholder={t('header.endDatePlaceholder')}
            value={dateRange.end ? dayjs(dateRange.end).format('YYYY-MM-DD') : undefined}
            onChange={handleEndDateChange}
            dateFormat='YYYY-MM-DD'
            disabled={isLoading}
            className='w-full'           
          />
        </Box>
      </Box>
    </Box>
  );
};

export default JourneyHeader;
